/**
 * Shared pose/keypoint types used by all games.
 * The Swift app sends one of two JSON shapes — both are normalized to PoseKeypoint[].
 */

import { normalizeNativeAxisForPose } from './normCoord.ts';

export type KeypointType =
  | 'HEAD'
  | 'NECK'
  | 'LEFT_EYE'
  | 'RIGHT_EYE'
  | 'LEFT_SHOULDER'
  | 'RIGHT_SHOULDER'
  | 'LEFT_ELBOW'
  | 'RIGHT_ELBOW'
  | 'LEFT_WRIST'
  | 'RIGHT_WRIST'
  | 'LEFT_HIP'
  | 'RIGHT_HIP'
  | 'LEFT_KNEE'
  | 'RIGHT_KNEE'
  | 'LEFT_ANKLE'
  | 'RIGHT_ANKLE';

export interface PoseKeypoint {
  /** Keypoint identifier */
  readonly type: KeypointType;
  /** Normalized horizontal position — 0 = left edge, 1 = right edge */
  readonly x: number;
  /** Normalized vertical position — 0 = top edge, 1 = bottom edge */
  readonly y: number;
  /** Detection confidence 0–1 (optional) */
  readonly confidence?: number;
}

export type PoseData = PoseKeypoint[];

// ── Format A (array) — preferred ─────────────────────────────────────────────
// [{ "type": "HEAD", "x": 0.5, "y": 0.5 }, ...]

// ── Format B (dictionary) — also accepted ────────────────────────────────────
// { "head": { "x": 0.5, "y": 0.5 }, "left_wrist": { "x": 0.3, "y": 0.7 } }
type DictPoint = { x: number; y: number; confidence?: number };
type DictPose = Partial<Record<string, DictPoint>>;

const DICT_KEY_MAP: Record<string, KeypointType> = {
  head: 'HEAD',
  neck: 'NECK',
  left_eye: 'LEFT_EYE',
  right_eye: 'RIGHT_EYE',
  left_shoulder: 'LEFT_SHOULDER',
  right_shoulder: 'RIGHT_SHOULDER',
  left_elbow: 'LEFT_ELBOW',
  right_elbow: 'RIGHT_ELBOW',
  left_wrist: 'LEFT_WRIST',
  right_wrist: 'RIGHT_WRIST',
  left_hip: 'LEFT_HIP',
  right_hip: 'RIGHT_HIP',
  left_knee: 'LEFT_KNEE',
  right_knee: 'RIGHT_KNEE',
  left_ankle: 'LEFT_ANKLE',
  right_ankle: 'RIGHT_ANKLE',
};

/**
 * Normalizes any incoming pose payload to PoseKeypoint[].
 * Accepts Format A (array) or Format B (dictionary).
 * Silently drops invalid entries.
 */
function mapPoseTypeString(type: string): KeypointType | null {
  const key = type.trim().toLowerCase().replace(/-/g, '_');
  return DICT_KEY_MAP[key] ?? null;
}

export function normalizePose(raw: unknown): PoseKeypoint[] {
  if (Array.isArray(raw)) {
    return raw.flatMap((item: unknown) => {
      if (!item || typeof item !== 'object') return [];
      const { type, x, y, confidence } = item as Record<string, unknown>;
      if (typeof type !== 'string' || typeof x !== 'number' || typeof y !== 'number') return [];
      const mapped = mapPoseTypeString(type);
      if (!mapped) return [];
      const nx = normalizeNativeAxisForPose(x);
      const ny = normalizeNativeAxisForPose(y);
      return [
        {
          type: mapped,
          x: nx,
          y: ny,
          ...(typeof confidence === 'number' ? { confidence } : {}),
        },
      ];
    });
  }

  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const dict = raw as DictPose;
    return Object.entries(dict).flatMap(([key, val]) => {
      const mapped = DICT_KEY_MAP[key.toLowerCase()];
      if (!mapped || !val || typeof val.x !== 'number' || typeof val.y !== 'number') return [];
      return [
        {
          type: mapped,
          x: normalizeNativeAxisForPose(val.x),
          y: normalizeNativeAxisForPose(val.y),
          ...(typeof val.confidence === 'number' ? { confidence: val.confidence } : {}),
        },
      ];
    });
  }

  return [];
}

/** Below this MediaPipe / native confidence (0–1), TV skips drawing that wrist for the frame. */
export const WRIST_MIN_CONFIDENCE = 0.18;

/** Extract the two ankle keypoints as { hand: 'Left'|'Right', x, y }. Used by Leg Kick. */
export function anklesAsHands(
  keypoints: PoseKeypoint[],
): Array<{ hand: 'Left' | 'Right'; x: number; y: number; confidence?: number }> {
  const result: Array<{ hand: 'Left' | 'Right'; x: number; y: number; confidence?: number }> = [];
  for (const k of keypoints) {
    if (k.type === 'LEFT_ANKLE') {
      result.push({ hand: 'Left', x: k.x, y: k.y, ...(k.confidence !== undefined ? { confidence: k.confidence } : {}) });
    } else if (k.type === 'RIGHT_ANKLE') {
      result.push({ hand: 'Right', x: k.x, y: k.y, ...(k.confidence !== undefined ? { confidence: k.confidence } : {}) });
    }
  }
  return result;
}

/**
 * Extract the two wrist keypoints as { hand: 'Left'|'Right', x, y }.
 * Used by Fruit Ninja to drive the blade trails.
 */
export function wristsAsHands(
  keypoints: PoseKeypoint[],
): Array<{ hand: 'Left' | 'Right'; x: number; y: number; confidence?: number }> {
  const result: Array<{ hand: 'Left' | 'Right'; x: number; y: number; confidence?: number }> = [];
  for (const k of keypoints) {
    if (k.type === 'LEFT_WRIST') {
      result.push({
        hand: 'Left',
        x: k.x,
        y: k.y,
        ...(k.confidence !== undefined ? { confidence: k.confidence } : {}),
      });
    } else if (k.type === 'RIGHT_WRIST') {
      result.push({
        hand: 'Right',
        x: k.x,
        y: k.y,
        ...(k.confidence !== undefined ? { confidence: k.confidence } : {}),
      });
    }
  }
  return result;
}
